import { computed, effect, Injectable, signal, untracked, WritableSignal } from '@angular/core';
import { Utilities } from '../../shared/utils/utilities';
import { BdoApiService } from './bdo-api.service';
import { Poll } from '../../../assets/js/com/ts_api_client';
import { ToastService } from './toast.service';
import { catchError, EMPTY } from 'rxjs';
import { Environment } from '../../../environments/environment';
import { STAGE } from '../enums/stage.enum';
import { DebugLogger } from '../../shared/utils/debugLogger';

export interface PollConfig {
  pollId: number;
  triggerPoint: PollTriggerPoint;
  extraContext?: string;
  delay?: number;
}

type PollTriggerPoint = 'Abschlag_anpassen' | 'Dokumentendownload' | 'Zaehlerstand' | 'PersoenlicheDaten';

@Injectable({
  providedIn: 'root'
})
export class PollService {
  public readonly showPollToast = computed(() => this.showPoll() && !this.toastService.currentToast());
  public readonly currentPollConfig: WritableSignal<PollConfig | null> = signal<PollConfig | null>(null);
  public readonly currentPoll = signal<Poll | null>(null);
  private readonly showPoll: WritableSignal<boolean> = signal<boolean>(false);
  private readonly pollApiError: WritableSignal<boolean> = signal<boolean>(false);

  private readonly defaultDelay = 3000; // 3 seconds

  constructor(
    private apiService: BdoApiService,
    private toastService: ToastService
  ) {
    effect(() => {
      const config = this.currentPollConfig();
      if (config) {
        untracked(() => {
          this.fetchPollData(config);
        });
      }
    });
  }

  public createPollShownCookie(): void {
    Utilities.createCookie('poll_shown', '1', 90);
  }

  /**
   * Checks if the poll can be shown and activates it after a delay if possible
   * Only shows the poll if API call succeeds and poll data is valid
   * @param config Configuration for the poll
   * @returns void
   */
  public activatePoll(config: PollConfig): void {
    if (!this.canShowPoll()) {
      return;
    }

    this.currentPollConfig.set(config);
    // Note: The actual poll display is handled in fetchPollData after successful API call and validation
  }

  /**
   * Closes the current poll and resets the configuration
   */
  public closePoll(): void {
    this.showPoll.set(false);
    this.currentPollConfig.set(null);
    this.pollApiError.set(false);
  }

  /**
   * Fetches poll data from API and only shows poll if successful and valid
   * @param config Configuration for the poll
   * @private
   */
  private fetchPollData(config: PollConfig): void {
    // Reset error state before attempting new API call
    this.pollApiError.set(false);

    this.apiService.getPoll(config.pollId).pipe(
      catchError((error) => {
        this.handlePollError('API call failed');
        return EMPTY;
      })
    ).subscribe((pollData) => {
      if (pollData) {
        // Validate poll data before proceeding
        if (this.isValidPollData(pollData)) {
          this.currentPoll.set(pollData);
          this.scheduleShowPoll(config);
        } else {
          this.handlePollError('Invalid poll data: empty questions array');
        }
      } else {
        this.handlePollError('No poll data received');
      }
    });
  }

  /**
   * Validates if the poll data contains valid questions
   * @param pollData The poll data to validate
   * @returns boolean indicating if the poll data is valid
   * @private
   */
  private isValidPollData(pollData: Poll): boolean {
    if (!pollData) {
      return false;
    }

    // Check if questions array exists and is not empty
    if (!pollData.questions || !Array.isArray(pollData.questions) || pollData.questions.length === 0) {
      return false;
    }

    return true;
  }

  /**
   * Handles poll errors by setting error state and resetting configuration
   * @param errorMessage The error message to log
   * @private
   */
  private handlePollError(errorMessage: string): void {

    if (Environment.stage !== STAGE.Prod) {
      DebugLogger.error(this.constructor.name, 'isLoggedIn()', errorMessage);
    }
    this.pollApiError.set(true);
    this.currentPollConfig.set(null); // Reset config on error
    this.currentPoll.set(null); // Reset poll data on error
  }

  /**
   * Schedules the poll to be shown after the configured delay
   * @param config Configuration for the poll
   * @private
   */
  private scheduleShowPoll(config: PollConfig): void {
    const delay = config.delay ?? this.defaultDelay;

    setTimeout(() => {
      // Double check that the poll data is still available and valid before showing
      if (this.currentPoll() && this.isValidPollData(this.currentPoll()!) && !this.pollApiError()) {
        this.createPollShownCookie();
        this.showPoll.set(true);
      }
    }, delay);
  }

  /**
   * Checks if the poll can be shown based on session and cookie state
   * @returns boolean indicating if the poll can be shown
   * @private
   */
  private canShowPoll(): boolean {
    return !Utilities.getCookie('poll_shown') && !this.pollApiError();
  }
}

export class PollServiceMock {
  public readonly showPollToast = signal(false);
  public readonly currentPollConfig: WritableSignal<PollConfig | null> = signal<PollConfig | null>(null);
  public readonly currentPoll: WritableSignal<Poll | null> = signal<Poll | null>(null);
  public createPollShownCookie(): void {}
  public activatePoll(_config: PollConfig): void {}
  public closePoll(): void {}
}
